import { useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import useAuthStore from "../store/useAuthStore";
import useLayoutStore from "../store/useLayoutStore";
import { TERMS_OF_SERVICE_TEXT } from "../constants/termsOfService";
import { getPasswordValidationErrors } from "../utils/passwordValidation";

const LanguageToggle = () => {
    const language = useLayoutStore((state) => state.language);
    const setLanguage = useLayoutStore((state) => state.setLanguage);

    const nextLanguage = language === "sr" ? "en" : "sr";
    const flagSrc = language === "sr" ? "/login/flag-sr.svg" : "/login/flag-en.svg";
    const label = language === "sr" ? "SR" : "EN";

    return (
        <button
            type="button"
            className="login-language-toggle"
            onClick={() => setLanguage(nextLanguage)}
            aria-label={language === "sr" ? "Switch to English" : "Prebaci na srpski"}
        >
            <AnimatePresence mode="wait">
                <motion.span
                    key={language}
                    className="login-language-option"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    transition={{ duration: 0.14, ease: "easeInOut" }}
                >
                    <img src={flagSrc} alt="" className="login-language-flag" />
                    <span>{label}</span>
                </motion.span>
            </AnimatePresence>
        </button>
    );
};

const LoginPage = () => {
    const requiresAccountSetup = useAuthStore(state => state.requiresAccountSetup);
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);

    const loginBackgroundImage = isDarkTheme ? '/login/bgBlack3.webp' : '/login/bgLight.webp';
    const loginBackgroundColor = isDarkTheme ? '#0f1117' : '#f4f6fb';

    return (
        <div
            className="app-container-login"
            style={{
                backgroundImage: `url("${loginBackgroundImage}")`,
                backgroundColor: loginBackgroundColor
            }}
        >
            <AnimatePresence mode="wait">
                {requiresAccountSetup ? (
                    <AccountSetupPanel key="account-setup" isDarkTheme={isDarkTheme} />
                ) : (
                    <LoginPanel key="login" isDarkTheme={isDarkTheme} />
                )}
            </AnimatePresence>
            <motion.div className="login-logo-container">
                <AnimatePresence mode="wait">
                    <motion.img
                        key={isDarkTheme ? "beo" : "text"}
                        src={isDarkTheme ? "/sidebar/PortirLogoTextBeo.svg" : "/sidebar/PortirLogoText.svg"}
                        alt="Portir Logo"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.14, ease: "easeInOut" }}
                    />
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

const LoginPanel = ({ isDarkTheme }) => {
    const { t } = useTranslation("login");
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const navigate = useNavigate();
    const login = useAuthStore(state => state.login);
    const toggleTheme = useLayoutStore(state => state.toggleTheme);

    const usernameRef = useRef(null);
    const passwordRef = useRef(null);

    const handleSubmit = (e) => {
        e.preventDefault();
        const username = usernameRef.current?.value || '';
        const password = passwordRef.current?.value || '';
        login(username, password, navigate);
    };

    return (
        <motion.div
            className="login-container"
            initial={{ opacity: 0, x: 240 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -240 }}
            transition={{ duration: 0.19, delay: 0.04, ease: "easeInOut" }}
        >
            <AnimatePresence>
                {showForgotPassword && (
                    <motion.div
                        className="login-back-action"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0 }}
                        transition={{ duration: 0.14, ease: "easeInOut" }}
                    >
                        <img
                            src="/login/Back.svg"
                            alt="Back"
                            className="login-action-icon"
                            onClick={() => setShowForgotPassword(false)}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
            <div className="login-language-actions">
                <LanguageToggle />
            </div>
            <div className="login-top-actions">
                <div className="theme-icon-wrapper" onClick={toggleTheme}>
                    <AnimatePresence mode="wait">
                        <motion.img
                            key={isDarkTheme ? "light" : "dark"}
                            src={isDarkTheme ? "/sidebar/lightTheme.svg" : "/sidebar/darkTheme.svg"}
                            alt="Toggle Theme"
                            className={`login-action-icon ${isDarkTheme ? "" : "login-light-theme-icon"}`}
                            initial={{ scale: 0 }}
                            animate={{ scale: isDarkTheme ? 1 : 0.9 }}
                            exit={{ scale: 0 }}
                            whileHover={{ scale: isDarkTheme ? 1.1 : 1 }}
                            whileTap={{ scale: 0.95 }}
                            transition={{ duration: 0.14, ease: "easeInOut" }}
                        />
                    </AnimatePresence>
                </div>
                <img
                    src="/login/ExitDoor.svg"
                    alt="Exit"
                    className="login-action-icon"
                    onClick={() => navigate("/")}
                />
            </div>
            <AnimatePresence mode="wait">
                {showForgotPassword ? (
                    <ForgotPassword
                        key="forgot-password"
                        isDarkTheme={isDarkTheme}
                        onResetComplete={() => setShowForgotPassword(false)}
                    />
                ) : (
                    <LoginForm
                        key="login-form"
                        usernameRef={usernameRef}
                        passwordRef={passwordRef}
                        onSubmit={handleSubmit}
                        onForgotPassword={() => setShowForgotPassword(true)}
                        isDarkTheme={isDarkTheme}
                    />
                )}
            </AnimatePresence>
            <div className={`login-footer ${isDarkTheme ? 'login-theme-dark' : 'login-theme-light'}`}>
                {t("poweredBy")} <span>Teamcad</span>
            </div>
        </motion.div>
    );
};

const AccountSetupPanel = ({ isDarkTheme }) => {
    const { t } = useTranslation("login");
    const navigate = useNavigate();
    const clearAccountSetup = useAuthStore(state => state.clearAccountSetup);
    const toggleTheme = useLayoutStore(state => state.toggleTheme);

    return (
        <motion.div
            className="account-setup-container"
            initial={{ opacity: 0, x: 240 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -240 }}
            transition={{ duration: 0.19, delay: 0.04, ease: "easeInOut" }}
        >
            <div className="login-back-action">
                <img
                    src="/login/Back.svg"
                    alt="Back"
                    className="login-action-icon"
                    onClick={clearAccountSetup}
                />
            </div>
            <div className="login-language-actions">
                <LanguageToggle />
            </div>
            <div className="login-top-actions">
                <div className="theme-icon-wrapper" onClick={toggleTheme}>
                    <AnimatePresence mode="wait">
                        <motion.img
                            key={isDarkTheme ? "light" : "dark"}
                            src={isDarkTheme ? "/sidebar/lightTheme.svg" : "/sidebar/darkTheme.svg"}
                            alt="Toggle Theme"
                            className={`login-action-icon ${isDarkTheme ? "" : "login-light-theme-icon"}`}
                            initial={{ scale: 0 }}
                            animate={{ scale: isDarkTheme ? 1 : 0.9 }}
                            exit={{ scale: 0 }}
                            whileHover={{ scale: isDarkTheme ? 1.1 : 1 }}
                            whileTap={{ scale: 0.95 }}
                            transition={{ duration: 0.14, ease: "easeInOut" }}
                        />
                    </AnimatePresence>
                </div>
                <img
                    src="/login/ExitDoor.svg"
                    alt="Exit"
                    className="login-action-icon"
                    onClick={() => navigate("/")}
                />
            </div>
            <AccountSetupForm isDarkTheme={isDarkTheme} />
            <div className={`login-footer ${isDarkTheme ? 'login-theme-dark' : 'login-theme-light'}`}>
                {t("poweredBy")} <span>Teamcad</span>
            </div>
        </motion.div>
    );
};

const LoginForm = ({ usernameRef, passwordRef, onSubmit, onForgotPassword, isDarkTheme }) => {
    const { t } = useTranslation("login");
    const error = useAuthStore(state => state.error);
    const resetError = useAuthStore(state => state.resetError);

    return (
        <motion.div
            key="login-form"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.19, ease: "easeInOut" }}
            className="login-form-content"
        >
            <motion.h2
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.19, delay: 0.04, ease: "easeInOut" }}
            >
                {t("title")}
            </motion.h2>
            <form onSubmit={onSubmit}>
                <AnimatePresence>
                    {error === 'Invalid credentials' && (
                        <motion.div
                            className="login-error"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ duration: 0.19, ease: "easeInOut" }}
                        >
                            {t("invalidCredentials")}
                        </motion.div>
                    )}
                </AnimatePresence>
                <motion.div
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 100 }}
                    transition={{ duration: 0.19, delay: 0.09, ease: "easeInOut" }}
                    className="input-with-icon">
                    <img src="/login/Mail.svg" alt="Email" className="input-icon" />
                    <input
                        ref={usernameRef}
                        type="text"
                        autoComplete="username"
                        onChange={() => {
                            if (error)
                                resetError();
                        }}
                        placeholder={t("emailPlaceholder")}
                        required
                    />
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.19, delay: 0.14, ease: "easeInOut" }}
                    className="input-with-icon">
                    <img src="/login/Lock.svg" alt="Lock" className="input-icon" />
                    <input
                        ref={passwordRef}
                        type="password"
                        autoComplete="current-password"
                        onChange={() => {
                            if (error)
                                resetError();
                        }}
                        placeholder={t("passwordPlaceholder")}
                    />
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.19, delay: 0.165, ease: "easeInOut" }}
                    className="login-forgot-password-wrapper"
                >
                    <button
                        type="button"
                        onClick={onForgotPassword}
                        className={`login-forgot-password-button ${isDarkTheme ? 'login-theme-dark' : 'login-theme-light'}`}
                    >
                        {t("forgotPassword")}
                    </button>
                </motion.div>
                <motion.button
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.19, delay: 0.19, ease: "easeInOut" }}
                    className={`login-button ${isDarkTheme ? "login-button-dark" : "login-button-light"}`}
                    type="submit">
                    {t("submit")}
                </motion.button>
            </form>
        </motion.div>
    );
};

const ForgotPassword = ({ isDarkTheme, onResetComplete }) => {
    const forgotPasswordMessage = useAuthStore(state => state.forgotPasswordMessage);
    const forgotPasswordError = useAuthStore(state => state.forgotPasswordError);

    const handleResendCode = () => {
        // Resetujemo stanje da se vrati na formu za unos email-a
        useAuthStore.setState({
            forgotPasswordMessage: null,
            forgotPasswordCodeError: null,
            forgotPasswordCodeSuccess: false,
            forgotPasswordError: null
        });
    }

    return (
        <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.19, ease: "easeInOut" }}
            className="forgot-password-content"
        >
            <AnimatePresence mode="wait">
                {forgotPasswordError !== null ? (
                    <ForgotPasswordError error={forgotPasswordError} />
                ) : forgotPasswordMessage === null ? (
                    <ForgotPasswordEmailForm isDarkTheme={isDarkTheme} />
                ) : (
                    <ForgotPasswordCodeForm
                        forgotPasswordMessage={forgotPasswordMessage}
                        isDarkTheme={isDarkTheme}
                        onResendCode={handleResendCode}
                        onResetComplete={onResetComplete}
                    />
                )}
            </AnimatePresence>
        </motion.div>
    );
}


const ForgotPasswordError = ({ error }) => {
    return (
        <motion.div
            key="error-message"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.19, ease: "easeInOut" }}
            className="forgot-password-message"
        >
            {error.message}
        </motion.div>
    );
};

const ForgotPasswordEmailForm = ({ isDarkTheme }) => {
    const { t } = useTranslation("login");
    const emailRef = useRef(null);
    const forgotPasswordSendMail = useAuthStore(state => state.forgotPasswordSendMail);
    const forgotPasswordLoading = useAuthStore(state => state.forgotPasswordLoading);

    const handleSubmitEmail = (e) => {
        if (forgotPasswordLoading) return;
        e.preventDefault();
        const email = emailRef.current?.value || '';
        // Resetuj grešku pre slanja novog zahteva
        useAuthStore.setState({ forgotPasswordError: null });
        forgotPasswordSendMail(email);
    };

    return (
        <motion.div
            key="email-form"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.19, ease: "easeInOut" }}
            style={{ overflow: 'visible' }}
        >
            <motion.h2
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.19, delay: 0.04, ease: "easeInOut" }}
            >
                {t("forgot.enterEmail")}
            </motion.h2>
            <motion.form
                onSubmit={handleSubmitEmail}
            >
                <motion.div
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.19, delay: 0.09, ease: "easeInOut" }}
                    className="input-with-icon"
                >
                    <img src="/login/Mail.svg" alt="Email" className="input-icon" />
                    <input
                        ref={emailRef}
                        type="email"
                        autoComplete="email"
                        placeholder={t("emailPlaceholder")}
                        required
                    />
                </motion.div>
                <motion.button
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.19, delay: 0.14, ease: "easeInOut" }}
                    className={`login-button ${isDarkTheme ? "login-button-dark" : "login-button-light"} ${forgotPasswordLoading ? 'disabled' : ''}`}
                    type="submit"
                    disabled={forgotPasswordLoading}
                >
                    {t("forgot.sendCode")}
                </motion.button>
            </motion.form>
        </motion.div>
    );
};

const ForgotPasswordCodeForm = ({ forgotPasswordMessage, isDarkTheme, onResendCode, onResetComplete }) => {
    const { t } = useTranslation("login");
    const forgotPasswordSubmitCode = useAuthStore(state => state.forgotPasswordSubmitCode);
    const forgotPasswordLoading = useAuthStore(state => state.forgotPasswordLoading);
    const forgotPasswordCodeSuccess = useAuthStore(state => state.forgotPasswordCodeSuccess);
    const forgotPasswordCodeError = useAuthStore(state => state.forgotPasswordCodeError);
    const codeRef = useRef(null);
    const [showSubmitButton, setShowSubmitButton] = useState(false);

    const handleCodeChange = (e) => {
        const value = e.target.value;
        setShowSubmitButton(value.length === 6);
        // Resetuj grešku kada korisnik počne da unosi kod
        if (forgotPasswordCodeError) {
            useAuthStore.setState({ forgotPasswordCodeError: null });
        }
    };

    const handleSubmitCode = (e) => {
        if (e) {
            e.preventDefault(); // Spreči default form submit behavior
        }
        if (forgotPasswordLoading) return;

        const code = codeRef.current.value;
        if (!code || code.length !== 6) {
            return; // Ne submit-uj ako kod nije kompletan
        }
        forgotPasswordSubmitCode(code);
    };

    return (
        <motion.div
            style={{ overflow: 'visible', marginTop: '19px' }}
            key="code-form"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.19, ease: "easeInOut" }}
        >
            {!forgotPasswordCodeSuccess ? (
                <>
                    <motion.h2
                        initial={{ opacity: 0, x: 100 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.19, delay: 0.04, ease: "easeInOut" }}
                        style={{ marginBottom: '9px' }}
                    >
                        {t("forgot.confirmCode")}
                    </motion.h2>
                    <motion.div
                        initial={{ opacity: 0, x: 100 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.19, delay: 0.09, ease: "easeInOut" }}
                        className="forgot-password-message-mini"
                    >
                        <Trans
                            i18nKey="forgot.codeSent"
                            ns="login"
                            values={{ email: forgotPasswordMessage.email }}
                            components={{ bold: <b /> }}
                        />
                    </motion.div>
                    <form onSubmit={handleSubmitCode}>
                        <AnimatePresence mode="wait">
                            {forgotPasswordCodeError && (
                                <motion.div
                                    className="login-error-reset-password"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    exit={{ scale: 0 }}
                                    transition={{ duration: 0.19, ease: "easeInOut" }}
                                >
                                    {forgotPasswordCodeError}
                                </motion.div>
                            )}
                        </AnimatePresence>
                        <motion.div
                            initial={{ opacity: 0, x: 100 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.19, delay: 0.14, ease: "easeInOut" }}
                            className="input-with-icon"
                            style={{ position: 'relative' }}
                        >
                            <img src="/login/Lock.svg" alt="Code" className="input-icon" />
                            <input
                                id="login-reset-code-input"
                                ref={codeRef}
                                type="text"
                                placeholder={t("forgot.codePlaceholder")}
                                required
                                maxLength={6}
                                onChange={handleCodeChange}
                                style={{ paddingRight: showSubmitButton ? '40px' : '' }}
                            />
                            <AnimatePresence mode="wait">
                                {showSubmitButton && (
                                    <motion.button
                                        type="button"
                                        onClick={handleSubmitCode}
                                        className={`login-reset-code-submit-button ${forgotPasswordLoading ? 'disabled' : ''}`}
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0 }}
                                        transition={{ duration: 0.14, ease: "easeInOut" }}
                                        disabled={forgotPasswordLoading}
                                    >
                                        <img
                                            src="/login/ExitDoor.svg"
                                            alt="Submit"
                                            className="login-reset-code-submit-icon"
                                        />
                                    </motion.button>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    </form>
                    <motion.div
                        initial={{ opacity: 0, x: 100 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.19, delay: 0.14, ease: "easeInOut" }}
                        className="forgot-password-resend"
                    >
                        <button
                            type="button"
                            onClick={onResendCode}
                            className={`forgot-password-resend-button ${isDarkTheme ? 'login-theme-dark' : 'login-theme-light'}`}
                        >
                            {t("forgot.resendCode")}
                        </button>
                    </motion.div>
                </>
            ) : (
                <ForgotPasswordResetForm isDarkTheme={isDarkTheme} onResetComplete={onResetComplete} />
            )}
        </motion.div>
    );
};

const ForgotPasswordResetForm = ({ isDarkTheme, onResetComplete }) => {
    const { t } = useTranslation("login");
    const forgotPasswordLoading = useAuthStore(state => state.forgotPasswordLoading);
    const resetPassword = useAuthStore(state => state.resetPassword);
    const forgotPasswordResetSuccess = useAuthStore(state => state.forgotPasswordResetSuccess);
    const forgotPasswordResetError = useAuthStore(state => state.forgotPasswordResetError);
    const newPasswordRef = useRef(null);
    const confirmPasswordRef = useRef(null);
    const [passwordError, setPasswordError] = useState(null);

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (forgotPasswordLoading) return;

        const newPassword = newPasswordRef.current?.value || '';
        const confirmPassword = confirmPasswordRef.current?.value || '';

        // Resetuj prethodne greške
        setPasswordError(null);
        useAuthStore.setState({ forgotPasswordResetError: null });

        // Provera da li se lozinke poklapaju
        if (newPassword !== confirmPassword) {
            setPasswordError(t("forgot.passwordsMismatch"));
            return;
        }

        // Validacija lozinke
        const passwordErrors = getPasswordValidationErrors(newPassword);
        if (passwordErrors.length > 0) {
            setPasswordError(t(passwordErrors[0]));
            return;
        }

        // Pozovi API za reset lozinke
        const success = await resetPassword(newPassword);

        if (success) {
            // Nakon uspešnog resetovanja, sačekaj malo i resetuj sve state-ove
            setTimeout(() => {
                useAuthStore.setState({
                    forgotPasswordMessage: null,
                    forgotPasswordError: null,
                    forgotPasswordCodeSuccess: false,
                    forgotPasswordResetSuccess: false,
                    forgotPasswordResetError: null,
                    forgotPasswordLoading: false
                });
                // Resetuj showForgotPassword da se prikaže login forma
                if (onResetComplete) {
                    onResetComplete();
                }
            }, 2014);
        }
    };

    return (
        <motion.div
            key="reset-form"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.19, ease: "easeInOut" }}
            style={{ overflow: 'visible' }}
        >
            <motion.h2
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.19, delay: 0.04, ease: "easeInOut" }}
                style={{ marginBottom: '34px' }}
            >
                {t("forgot.enterNewPassword")}
            </motion.h2>

            {forgotPasswordResetSuccess ? (
                <motion.div
                    key="reset-success"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    transition={{ duration: 0.19, ease: "easeInOut" }}
                    className="forgot-password-message"
                >
                    {t("forgot.resetSuccess")}
                </motion.div>
            ) : (
                <motion.form
                    onSubmit={handleResetPassword}
                >
                    <AnimatePresence>
                        {(passwordError || forgotPasswordResetError) && (
                            <motion.div
                                className="login-error-reset-password"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0 }}
                                transition={{ duration: 0.19, ease: "easeInOut" }}
                            >
                                {passwordError || forgotPasswordResetError}
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <motion.div
                        initial={{ opacity: 0, x: 100 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.19, delay: 0.09, ease: "easeInOut" }}
                        className="input-with-icon"
                    >
                        <img src="/login/Lock.svg" alt="Password" className="input-icon" />
                        <input
                            ref={newPasswordRef}
                            type="password"
                            placeholder={t("forgot.newPasswordPlaceholder")}
                            autoComplete="new-password"
                            required
                            minLength={8}
                            onChange={() => {
                                if (passwordError) setPasswordError(null);
                                if (forgotPasswordResetError) useAuthStore.setState({ forgotPasswordResetError: null });
                            }}
                        />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: 100 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.19, delay: 0.14, ease: "easeInOut" }}
                        className="input-with-icon"
                    >
                        <img src="/login/Lock.svg" alt="Confirm Password" className="input-icon" />
                        <input
                            ref={confirmPasswordRef}
                            type="password"
                            placeholder={t("forgot.confirmPasswordPlaceholder")}
                            autoComplete="new-password"
                            required
                            minLength={8}
                            onChange={() => {
                                if (passwordError) setPasswordError(null);
                                if (forgotPasswordResetError) useAuthStore.setState({ forgotPasswordResetError: null });
                            }}
                        />
                    </motion.div>
                    <motion.button
                        initial={{ opacity: 0, x: 100 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.19, delay: 0.19, ease: "easeInOut" }}
                        className={`login-button ${isDarkTheme ? "login-button-dark" : "login-button-light"} ${forgotPasswordLoading ? 'disabled' : ''}`}
                        type="submit"
                        disabled={forgotPasswordLoading}
                    >
                        {t("forgot.resetPassword")}
                    </motion.button>
                </motion.form>
            )}
        </motion.div>
    );
};

const AccountSetupForm = ({ isDarkTheme }) => {
    const { t } = useTranslation("login");
    const navigate = useNavigate();
    const accountSetupEmail = useAuthStore(state => state.accountSetupEmail);
    const accountSetupLoading = useAuthStore(state => state.accountSetupLoading);
    const completeAccountSetup = useAuthStore(state => state.completeAccountSetup);
    const newPasswordRef = useRef(null);
    const confirmPasswordRef = useRef(null);
    const [passwordError, setPasswordError] = useState(null);
    const [termsAccepted, setTermsAccepted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (accountSetupLoading) return;

        const newPassword = newPasswordRef.current?.value || '';
        const confirmPassword = confirmPasswordRef.current?.value || '';

        setPasswordError(null);
        useAuthStore.setState({ accountSetupError: null });

        if (!termsAccepted) {
            setPasswordError(t("setup.mustAcceptTerms"));
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError(t("setup.passwordsMismatch"));
            return;
        }

        const passwordErrors = getPasswordValidationErrors(newPassword);
        if (passwordErrors.length > 0) {
            setPasswordError(t(passwordErrors[0]));
            return;
        }

        const success = await completeAccountSetup(newPassword, termsAccepted, navigate);
        if (!success) {
            const serverError = useAuthStore.getState().accountSetupError;
            if (serverError) {
                setPasswordError(serverError);
            }
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.19, ease: "easeInOut" }}
            className="login-account-setup-content"
        >
            <div className="login-account-setup-layout">
                <div className="login-account-setup-form-panel">
                    <motion.h2
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.19, delay: 0.04, ease: "easeInOut" }}
                    >
                        {t("setup.title")}
                    </motion.h2>

                    {accountSetupEmail && (
                        <motion.p
                            className={`login-account-setup-email ${isDarkTheme ? 'login-theme-dark' : 'login-theme-light'}`}
                            initial={{ opacity: 0, x: 40 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.19, delay: 0.06, ease: "easeInOut" }}
                        >
                            {accountSetupEmail}
                        </motion.p>
                    )}

                    <form onSubmit={handleSubmit} className="login-account-setup-form">
                        <AnimatePresence>
                            {passwordError && (
                                <motion.div
                                    className="login-error login-account-setup-error"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    exit={{ scale: 0 }}
                                    transition={{ duration: 0.19, ease: "easeInOut" }}
                                >
                                    {passwordError}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <motion.div
                            initial={{ opacity: 0, x: 40 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.19, delay: 0.09, ease: "easeInOut" }}
                            className="input-with-icon login-account-setup-input"
                        >
                            <img src="/login/Lock.svg" alt="Password" className="input-icon" />
                            <input
                                ref={newPasswordRef}
                                type="password"
                                placeholder={t("setup.newPasswordPlaceholder")}
                                autoComplete="new-password"
                                onChange={() => passwordError && setPasswordError(null)}
                            />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 40 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.19, delay: 0.12, ease: "easeInOut" }}
                            className="input-with-icon login-account-setup-input"
                        >
                            <img src="/login/Lock.svg" alt="Confirm Password" className="input-icon" />
                            <input
                                ref={confirmPasswordRef}
                                type="password"
                                placeholder={t("setup.confirmPasswordPlaceholder")}
                                autoComplete="new-password"
                                onChange={() => passwordError && setPasswordError(null)}
                            />
                        </motion.div>

                        <motion.label
                            initial={{ opacity: 0, x: 40 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.19, delay: 0.15, ease: "easeInOut" }}
                            className={`login-account-setup-checkbox ${isDarkTheme ? 'login-account-setup-checkbox-dark' : ''}`}
                        >
                            <input
                                type="checkbox"
                                className="login-account-setup-checkbox-input"
                                checked={termsAccepted}
                                onChange={(e) => {
                                    setTermsAccepted(e.target.checked);
                                    if (passwordError) setPasswordError(null);
                                }}
                            />
                            <span className="login-account-setup-checkbox-box" aria-hidden="true" />
                            <span className="login-account-setup-checkbox-text">{t("setup.termsAccept")}</span>
                        </motion.label>

                        <motion.button
                            initial={{ opacity: 0, x: 40 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.19, delay: 0.18, ease: "easeInOut" }}
                            className={`login-button login-account-setup-submit ${isDarkTheme ? "login-button-dark" : "login-button-light"} ${accountSetupLoading ? 'disabled' : ''}`}
                            type="submit"
                            disabled={accountSetupLoading}
                        >
                            {t("setup.continue")}
                        </motion.button>
                    </form>
                </div>

                <div className={`login-account-setup-terms-panel ${isDarkTheme ? 'login-account-setup-terms-panel-dark' : ''}`}>
                    <h3 className="login-account-setup-terms-title">{t("setup.termsTitle")}</h3>
                    <div className={`login-account-setup-terms-outer ${isDarkTheme ? 'login-account-setup-terms-outer-dark' : ''}`}>
                        <div className="login-account-setup-terms-scroll">
                            <pre>{TERMS_OF_SERVICE_TEXT}</pre>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default LoginPage;
